import React from "react"
import { Navigation, useNavigationContext } from "@kit/design/navigation"
import { Menu } from "@kit/design/menu"
import { CountBadge } from "@kit/design/count-badge"
import { useAccountOptions } from "./utils/use-account-options"
import { navName, trackNavClick } from "./utils/track-nav-click"
import { useVerticalNavOptIn } from "./utils/use-vertical-nav-optin"
import HorizontalNavIcon from "./assets/horizontal-nav.svg"

const asLink =
  (href, attrs = {}) =>
  ({ children, ...props }) => (
    <a href={href} {...attrs} {...props}>
      {children}
    </a>
  )

const accessLabel = account => {
  if (account.unpaid) return "[no access, unpaid] "
  if (account.trial_expired) return "[no access, trial expired] "
  return ""
}

const sortedAccounts = accounts =>
  accounts
    .map(account => ({
      ...account,
      label: `${accessLabel(account)}${account.name || account.email}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

function handleAIAssistant() {
  /* eslint-disable-next-line no-undef */
  if (typeof Intercom === "undefined" || !Intercom) {
    window.open("https://help.kit.com/", "_blank")
    return
  }
  /* eslint-disable-next-line no-undef */
  Intercom("showNewMessage")
}

export function UserAccountMenu({ user, onWhatsNewClick, whatsNewCount }) {
  const { collapsed, mobile } = useNavigationContext()
  const { accounts } = useAccountOptions(user)
  const { optInEnabled, switchToOldNav } = useVerticalNavOptIn()
  const name = user.name || user.email

  // Account-switch links are intentionally not tracked, matching the old nav.
  const track =
    (label, elementType = "link") =>
    () =>
      trackNavClick({
        name: navName(label),
        text: label,
        elementType,
        location: "account_menu",
        isMobile: mobile
      })

  return (
    <Menu>
      <Menu.Trigger render={<Navigation.AccountTrigger name={name} />} />
      <Menu.Content matchTriggerWidth={!collapsed} sideOffset={8}>
        <Menu.Group>
          {user.admin && (
            <Menu.Submenu>
              <Menu.SubmenuTrigger openOnHover>
                <Menu.ItemIcon name="admin" />
                <Menu.ItemLabel>Admin</Menu.ItemLabel>
              </Menu.SubmenuTrigger>
              <Menu.SubmenuContent>
                <Menu.Group>
                  <Menu.Item
                    render={asLink("/admin")}
                    onClick={track("Go to Admin")}
                  >
                    <Menu.ItemIcon name="admin" />
                    <Menu.ItemLabel>Go to Admin</Menu.ItemLabel>
                  </Menu.Item>
                  <Menu.Item
                    render={asLink(`/admin/accounts/${user.account_id}`)}
                    onClick={track("View account in admin")}
                  >
                    <Menu.ItemIcon name="view" />
                    <Menu.ItemLabel>View account in admin</Menu.ItemLabel>
                  </Menu.Item>
                </Menu.Group>
              </Menu.SubmenuContent>
            </Menu.Submenu>
          )}
          <Menu.Submenu>
            <Menu.SubmenuTrigger openOnHover>
              <Menu.ItemIcon name="question-circle" />
              <Menu.ItemLabel>Get help</Menu.ItemLabel>
            </Menu.SubmenuTrigger>
            <Menu.SubmenuContent>
              <Menu.Group>
                <Menu.Item
                  onClick={() => {
                    track("AI assistant", "button")()
                    handleAIAssistant()
                  }}
                >
                  <Menu.ItemIcon name="sparkle" />
                  <Menu.ItemLabel>AI assistant</Menu.ItemLabel>
                </Menu.Item>
                <Menu.Item
                  render={asLink("mailto:help@kit.com")}
                  onClick={track("Contact support")}
                >
                  <Menu.ItemIcon name="support" />
                  <Menu.ItemLabel>Contact support</Menu.ItemLabel>
                </Menu.Item>
              </Menu.Group>
              <Menu.Divider />
              <Menu.Group>
                <Menu.Item
                  render={asLink("https://help.kit.com/", {
                    target: "_blank",
                    rel: "noreferrer"
                  })}
                  onClick={track("Documentation")}
                >
                  <Menu.ItemIcon name="book" />
                  <Menu.ItemLabel>Documentation</Menu.ItemLabel>
                </Menu.Item>
                <Menu.Item
                  render={asLink("/community")}
                  onClick={track("Community")}
                >
                  <Menu.ItemIcon name="chat-conversation" />
                  <Menu.ItemLabel>Community</Menu.ItemLabel>
                </Menu.Item>
                <Menu.Item
                  render={asLink("https://learn.kit.com/courses", {
                    target: "_blank",
                    rel: "noreferrer"
                  })}
                  onClick={track("Creator University")}
                >
                  <Menu.ItemIcon name="education" />
                  <Menu.ItemLabel>Creator University</Menu.ItemLabel>
                </Menu.Item>
              </Menu.Group>
            </Menu.SubmenuContent>
          </Menu.Submenu>
          <Menu.Item
            onClick={() => {
              track("What's new", "button")()
              onWhatsNewClick()
            }}
          >
            <Menu.ItemIcon name="megaphone" />
            <Menu.ItemLabel>{"What's new"}</Menu.ItemLabel>
            {whatsNewCount > 0 && (
              <Menu.ItemTrailing>
                <CountBadge count={whatsNewCount} />
              </Menu.ItemTrailing>
            )}
          </Menu.Item>
          {optInEnabled && (
            <Menu.Item
              onClick={() => {
                track("Revert to old navigation", "button")()
                switchToOldNav()
              }}
            >
              <HorizontalNavIcon
                data-slot="menu-item-icon"
                aria-hidden="true"
                className="size-6"
              />
              <Menu.ItemLabel>Revert to old navigation</Menu.ItemLabel>
            </Menu.Item>
          )}
        </Menu.Group>
        {user.has_multi_account && (
          <>
            <Menu.Divider />
            <Menu.Group>
              <Menu.Header>Switch accounts</Menu.Header>
              {sortedAccounts(accounts).map(account => (
                <Menu.Item
                  key={account.id}
                  render={asLink(`/account_users/${account.id}/switch`, {
                    "data-method": "post"
                  })}
                >
                  <Menu.ItemAvatar name={account.name || account.email} />
                  <Menu.ItemLabel>{account.label}</Menu.ItemLabel>
                </Menu.Item>
              ))}
            </Menu.Group>
          </>
        )}
        <Menu.Divider />
        <Menu.Group>
          <Menu.Item
            render={asLink("/users/logout", { "data-method": "delete" })}
            onClick={track("Log out")}
          >
            <Menu.ItemIcon name="log-out" />
            <Menu.ItemLabel>Log out</Menu.ItemLabel>
          </Menu.Item>
        </Menu.Group>
      </Menu.Content>
    </Menu>
  )
}
